<script setup lang="ts">
/** Ortak metin alani — FW-126 dort adimin da (email/password) tek satirlik
 * giris alanlari bu bileseni paylasir; tekrar eden label+input+hata iskeleti
 * tek yerde yasar. */
defineProps<{
  readonly id: string
  readonly label: string
  readonly modelValue: string
  readonly type?: string
  readonly placeholder?: string
  readonly autocomplete?: string
  readonly errorMessage?: string | null
}>()

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div class="fw-auth-field">
    <label :for="id">{{ label }}</label>
    <div class="fw-auth-field__control">
      <input
        :id="id"
        :type="type ?? 'text'"
        :value="modelValue"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :aria-invalid="Boolean(errorMessage)"
        :class="{ 'fw-auth-field__input--with-trailing': $slots.trailing }"
        @input="onInput"
      >
      <span
        v-if="$slots.trailing"
        class="fw-auth-field__trailing"
      >
        <slot name="trailing" />
      </span>
    </div>
    <p
      v-if="errorMessage"
      class="fw-auth-field__error"
      role="alert"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped>
/* design/current/Auth v2.dc.html'de label+input dogrudan butonun kardesi —
 * aradaki 18px bosluk yalniz butonun kendi margin-top'undan gelir. Burada
 * eskiden bir margin-bottom:4px vardi; buton `display:inline-block`
 * oldugu icin (UA varsayilani) komsu margin'ler COLLAPSE OLMUYOR ve
 * olculen bosluk 22px'e cikiyordu (fark +4px). Tasarimda karsiligi olmayan
 * bu deger kaldirildi. */
.fw-auth-field label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--fw-text-secondary);
  margin-bottom: 7px;
}

.fw-auth-field__control {
  position: relative;
}

.fw-auth-field input {
  width: 100%;
  height: 44px;
  padding: 0 13px;
  border-radius: 8px;
  border: 1px solid var(--fw-border);
  background: var(--fw-bg-page);
  color: var(--fw-text-primary);
}

.fw-auth-field__input--with-trailing {
  padding-right: 44px;
}

.fw-auth-field input[aria-invalid='true'] {
  border-color: var(--fw-error);
}

/* OLCULDU (Playwright, gercek Chromium) — packages/tokens/src/tokens.css'teki
 * kuresel `input:focus` kurali (specificity 0,0,1,1) burada HICBIR ZAMAN
 * KAZANAMAZ: Vue'nun scoped-style [data-v-x] ozniteligi yukaridaki
 * `.fw-auth-field input` kuralini 0,0,2,1'e cikarir ve border-color orada
 * zaten tanimli (border shorthand'i icinde). Sonuc: odakta kenarlik rengi
 * HIC DEGISMIYORDU, tarayicinin varsayilan beyaz UA halkasi goruluyordu.
 * design/current/Auth v2.dc.html satir 17 ile birebir; bu bilesen kendi
 * border'ini tanimladigi icin :focus halini de kendisi tasir (kuresel
 * kural diger, kendi border'ini tanimlamayan input'lar icin YEDEK kalir). */
.fw-auth-field input:focus {
  outline: none;
  border-color: var(--fw-primary-light);
  box-shadow: 0 0 0 3px rgba(59, 111, 232, 0.22);
}

.fw-auth-field__trailing {
  position: absolute;
  right: 10px;
  top: 0;
  height: 44px;
  display: flex;
  align-items: center;
}

.fw-auth-field__error {
  display: flex;
  gap: 8px;
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--fw-error);
}
</style>
